import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Search, Menu, X, ChevronLeft, ChevronRight, Star, MapPin, Phone, Clock } from 'lucide-react';

// ── Brand assets ──────────────────────────────────────────────────────────────
const LOGO = 'https://plada-uploads.s3.us-east-1.amazonaws.com/tenant-logos/logo-cdp-2026-02-02T17-00-31-824Z.png';
const FAVICON_LIGHT = 'https://plada-uploads.s3.us-east-1.amazonaws.com/tenant-logos/lmt-favicon-e7cb355a-6e9a-44bd-84f4-fe81afab34df.png';
const FAVICON_DARK = 'https://plada-uploads.s3.us-east-1.amazonaws.com/tenant-logos/lmt-favicon-dark-92f9d13f-e33c-46df-9372-7e78ae918e8c.png';
const HERO_VIDEO = 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/c0af11cd-8c16-4ef8-a795-878e10ed0969-centro-deportivo-hero.mp4';
const WHATSAPP_URL = 'https://wa.me/573155502034?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20Centro%20Deportivo%20Luz%20Mery%20Trist%C3%A1n';

// 14 client photos (in upload order)
const IMG = {
  i1:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/254cad69-1121-442e-9144-0a1d3497eaf6-424828477_761822375343814_763635126573484318_n.jpg',
  i2:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/3524d820-716a-4c54-8667-15cc3bfd1fb6-590767301_18440438200103185_4436729330208533622_n.jpg',
  i3:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/24957955-dd32-4107-8a2a-6c10353d0d9b-590975896_1912184059365691_7654512364792514290_n.jpg',
  i4:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/5eeb9e64-1f8f-42a5-b52f-1ed291853ecd-591165460_18440556784103185_7603150527634580195_n.jpg',
  i5:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/966c56d5-0502-4602-867d-f5abf18af1af-604350691_2120061258750198_1203450402641297899_n.jpg',
  i6:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/e17b1daf-837d-4d43-8a49-724a64e04463-617083878_18446755246103185_6575424857677921059_n.jpg',
  i7:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/431d90fe-4235-4a95-a182-1ef972bb3acd-627681094_18450946573103185_349221444458143770_n.jpg',
  i8:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/73b1f5fe-dd94-43a3-83d6-02ce91c09c5a-629187459_1645021389988968_3194774696765774087_n.jpg',
  i9:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/f41bb54e-6341-4caa-9d10-5db9c14ee0a8-630110635_909930831404779_2087257616712377500_n.jpg',
  i10: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/f8dc668a-449f-4441-a4f1-3ff86d55ae95-641590778_18455564371103185_4572731300701723613_n.jpg',
  i11: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/d454744e-ab15-47a6-b6e6-5678e21ae374-645939604_18456132274103185_387891363858356031_n.jpg',
  i12: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/494ca1e6-b8d5-4684-bd30-39ec72f16cca-649673352_18457091149103185_5742366071149467324_n.jpg',
  i13: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/ccfe1dc1-f8ce-43f8-9bed-e20246b34ef9-656294611_18460381012103185_3250344763669457348_n.jpg',
  i14: 'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/afe46500-60aa-421c-aab7-8c272c69f39e-658515711_18459975544103185_109878652209904239_n.jpg',
};

// ── Navbar ────────────────────────────────────────────────────────────────────
const navLinks = [
  { label: 'Nosotros', href: '#about' },
  { label: 'Programas', href: '#programs' },
  { label: 'Instalaciones', href: '#facilities' },
  { label: 'Testimonios', href: '#testimonials' },
  { label: 'Contacto', href: '#contact' },
];

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    el?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10 h-[60px] flex items-center justify-between">
        <button onClick={() => handleNav('#hero')} className="flex items-center gap-0 shrink-0">
          <img src={LOGO} alt="Centro Deportivo Luz Mery Tristan" className="h-[36px] w-auto object-contain flex-shrink-0" />
        </button>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="text-[#001489] hover:text-[#009edd] transition-colors relative group"
              style={{ fontSize: '15px', fontWeight: 500 }}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 group-hover:w-full h-[2px] bg-[#fd3eb5] transition-all duration-300" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <button className="hidden sm:flex items-center gap-1 text-[#001489] hover:text-[#009edd] transition-colors" aria-label="Buscar">
            <Search size={20} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => handleNav('#contact')}
            className="hidden sm:block bg-[#001489] text-white px-5 py-2 rounded-full hover:bg-[#009edd] transition-colors"
            style={{ fontSize: '13px', fontWeight: 600 }}
          >
            Únete
          </button>
          <button className="lg:hidden text-[#001489]" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menú">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 absolute top-full left-0 right-0 shadow-2xl">
          <div className="px-6 py-6 space-y-0">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="block w-full text-left text-[#001489] py-4 border-b border-gray-100"
                style={{ fontSize: '20px', fontWeight: 600 }}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleNav('#contact')}
              className="w-full mt-6 bg-[#001489] text-white py-4 rounded-full"
              style={{ fontSize: '16px', fontWeight: 600 }}
            >
              Inscríbete Ahora
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section id="hero" className="relative w-full bg-[#f0f6ff]">
      <div className="relative w-full" style={{ height: 'calc(100vh - 96px)', minHeight: '500px' }}>
        <video
          src={HERO_VIDEO}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={IMG.i1}
          className="w-full h-full object-cover"
          aria-label="Atletas Centro Deportivo Luz Mery Tristan"
        />
        <div className="absolute inset-0 bg-[#001489]/50" />
        <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-16 pb-16 lg:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="max-w-3xl"
          >
            <h1
              className="text-white mb-5"
              style={{
                fontSize: '75px',
                fontWeight: 900,
                lineHeight: 0.9,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                fontStyle: 'italic',
              }}
            >
              Muévete<br />en Familia.
            </h1>
            <p
              className="text-white mb-2"
              style={{
                fontSize: 'clamp(16px, 2vw, 22px)',
                fontWeight: 800,
                letterSpacing: '0.01em',
                textTransform: 'uppercase',
              }}
            >
              Centro Deportivo Luz Mery Tristán
            </p>
            <p
              className="text-white/80 mb-7 max-w-lg"
              style={{ fontSize: 'clamp(14px, 1.5vw, 17px)', lineHeight: 1.6, fontWeight: 400 }}
            >
              Natación y patinaje para niños y adultos en Cali. Más de 30 años formando deportistas.
            </p>
            <div className="flex flex-wrap gap-6">
              <button
                onClick={() => document.querySelector('#programs')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-white border-b-2 border-[#fd3eb5] pb-1 hover:text-[#fd3eb5] transition-colors"
                style={{ fontSize: '16px', fontWeight: 600 }}
              >
                Ver Programas
              </button>
              <button
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-white border-b-2 border-[#009edd] pb-1 hover:text-[#009edd] transition-colors"
                style={{ fontSize: '16px', fontWeight: 600 }}
              >
                Clase Gratis
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── About ─────────────────────────────────────────────────────────────────────
const stats = [
  { value: '30+', label: 'AÑOS' },
  { value: '5K+', label: 'FAMILIAS' },
  { value: '2', label: 'DISCIPLINAS' },
  { value: '200+', label: 'MEDALLISTAS' },
];

function AboutSection() {
  return (
    <section id="about" className="bg-white">
      <div className="grid lg:grid-cols-2 min-h-[70vh]">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden"
          style={{ minHeight: '400px' }}
        >
          <img src={IMG.i2} alt="Familias en el centro deportivo" className="w-full h-full object-cover" />
        </motion.div>
        <div className="flex flex-col justify-center px-8 lg:px-16 xl:px-24 py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p
              className="text-[#009edd] mb-3"
              style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
            >
              Nosotros
            </p>
            <h2
              className="text-[#001489] mb-6"
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
              }}
            >
              Un lugar para<br />toda la familia.
            </h2>
            <p className="text-gray-600 mb-4" style={{ fontSize: '15px', lineHeight: 1.7 }}>
              Inspirados en la campeona olímpica Luz Mery Tristán, llevamos más de 30 años formando deportistas y uniendo familias a través del deporte en Cali.
            </p>
            <p className="text-gray-600 mb-8" style={{ fontSize: '15px', lineHeight: 1.7 }}>
              Aquí niños, jóvenes y adultos entrenan juntos en un ambiente seguro, profesional y divertido.
            </p>
            <button
              onClick={() => document.querySelector('#programs')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-[#001489] underline underline-offset-4 decoration-[#fd3eb5] hover:text-[#009edd] hover:no-underline transition-all"
              style={{ fontSize: '14px', fontWeight: 600 }}
            >
              Conoce los programas →
            </button>
          </motion.div>
        </div>
      </div>
      <div className="bg-[#001489] text-white">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-white mb-1" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, letterSpacing: '-0.02em' }}>
                  {stat.value}
                </p>
                <p className="text-gray-400" style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.1em' }}>
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Programs (Natación + Patinaje, real prices/schedules) ─────────────────────
interface PlanTier { name: string; price: string; }
interface PlanGroup {
  title: string;
  matricula?: string;
  tiers: PlanTier[];
  schedule?: string[];
}
interface DisciplineData {
  title: string;
  subtitle: string;
  image: string;
  groups: PlanGroup[];
}

const disciplines: DisciplineData[] = [
  {
    title: 'Natación',
    subtitle: 'Piscina semiolímpica · Niños · Bebés · Adultos',
    image: IMG.i3,
    groups: [
      {
        title: 'Niños y Bebés',
        matricula: '$99.200',
        tiers: [
          { name: 'Clase ocasional', price: '$72.600' },
          { name: '4 clases', price: '$272.600' },
          { name: '8 clases', price: '$420.600' },
          { name: '12 clases', price: '$624.400' },
          { name: 'Semilleros', price: '$406.300' },
        ],
      },
      {
        title: 'Adultos',
        matricula: '$71.900',
        tiers: [
          { name: 'Clase ocasional', price: '$72.600' },
          { name: '4 clases', price: '$159.700' },
          { name: '8 clases', price: '$297.300' },
          { name: '12 clases', price: '$389.400' },
          { name: '20 clases', price: '$660.700' },
        ],
      },
      {
        title: 'Combinados con Gym (Adultos)',
        tiers: [
          { name: 'Gym + natación 1 vez/semana', price: '$381.000' },
          { name: 'Gym + natación 2 veces/semana', price: '$448.000' },
          { name: 'Gym + natación full', price: '$500.000' },
        ],
      },
      {
        title: 'Personalizados',
        tiers: [
          { name: '1 clase', price: '$125.100' },
          { name: 'Combo 4 clases', price: '$435.500' },
          { name: 'Combo 8 clases', price: '$869.600' },
          { name: 'Combo 12 clases', price: '$1.181.000' },
        ],
        schedule: [
          'Lunes a viernes: 7:00 - 8:00 p.m.',
          'Lunes, miércoles y viernes: 12:30 - 1:30 p.m.',
          'Martes a viernes: 7:30 - 8:30 a.m.',
          'Sábados: 7:30 - 8:30 a.m.',
        ],
      },
    ],
  },
  {
    title: 'Patinaje',
    subtitle: 'Pista profesional · Niños · Artístico · Adultos',
    image: IMG.i6,
    groups: [
      {
        title: 'Niños',
        matricula: '$99.200',
        tiers: [
          { name: 'Clase ocasional', price: '$59.900' },
          { name: '4 clases', price: '$190.400' },
          { name: '8 clases', price: '$274.200' },
          { name: '12 clases', price: '$335.200' },
          { name: 'Especialización y novatos', price: '$375.100' },
        ],
        schedule: [
          'Lunes a viernes: 4:00 – 5:00 p.m. y 5:00 – 6:00 p.m.',
          'Sábados: 9:00 – 10:00 a.m., 10:00 – 11:00 a.m., 11:00 – 12:00 m.',
        ],
      },
      {
        title: 'Artístico',
        matricula: '$85.500',
        tiers: [
          { name: 'Clase ocasional', price: '$57.000' },
          { name: '4 clases al mes', price: '$241.400' },
          { name: '8 clases al mes', price: '$260.000' },
          { name: '12 clases al mes', price: '$296.400' },
        ],
        schedule: ['Martes, miércoles y viernes: 5:30 - 7:00 p.m.'],
      },
      {
        title: 'Adultos',
        matricula: '$71.900',
        tiers: [
          { name: 'Clase ocasional', price: '$59.900' },
          { name: '4 clases', price: '$144.800' },
          { name: '8 clases', price: '$209.800' },
          { name: '12 clases al mes', price: '$261.100' },
          { name: 'Todos los días', price: '$291.900' },
        ],
      },
      {
        title: 'Combinados con Gym (Adultos)',
        tiers: [
          { name: 'Gym + patinaje 1 vez/semana', price: '$365.500' },
          { name: 'Gym + patinaje 2 veces/semana', price: '$404.000' },
          { name: 'Gym + patinaje full', price: '$423.500' },
        ],
        schedule: ['Viernes: 6:30 - 8:00 p.m.', 'Sábados: 10:00 - 11:00 a.m.'],
      },
      {
        title: 'Personalizados',
        tiers: [
          { name: '1 clase', price: '$99.800' },
          { name: 'Combo 4 clases', price: '$359.100' },
          { name: 'Combo 8 clases', price: '$682.300' },
          { name: 'Combo 12 clases', price: '$897.800' },
        ],
      },
    ],
  },
];

function FlipCard({ data, index }: { data: DisciplineData; index: number }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="cursor-pointer"
      style={{ perspective: '1200px' }}
      onClick={() => setFlipped((f) => !f)}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div
        className="relative w-full transition-transform duration-700"
        style={{
          height: 'min(85vh, 900px)',
          minHeight: '600px',
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* FRONT */}
        <div
          className="absolute inset-0 rounded-lg overflow-hidden"
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#001489]/95 via-[#001489]/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10">
            <h3
              className="text-white"
              style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 1 }}
            >
              {data.title}
            </h3>
            <p className="text-white/80 mt-2" style={{ fontSize: 'clamp(14px, 1.4vw, 17px)' }}>
              {data.subtitle}
            </p>
            <p
              className="text-[#009edd] mt-4"
              style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
            >
              Toca para ver precios →
            </p>
          </div>
        </div>
        {/* BACK */}
        <div
          className="absolute inset-0 rounded-lg overflow-hidden bg-[#001489] flex flex-col"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <div className="px-8 lg:px-10 pt-8 pb-5 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
            <div>
              <h3
                className="text-white"
                style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 1 }}
              >
                {data.title}
              </h3>
              <p className="text-[#009edd] mt-2" style={{ fontSize: '14px' }}>{data.subtitle}</p>
            </div>
            <button
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors shrink-0"
              onClick={(e) => { e.stopPropagation(); setFlipped(false); }}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
          <div className="px-8 lg:px-10 pt-6 pb-4 flex-1 centro-scroll" onWheel={(e) => e.stopPropagation()}>
            {data.groups.map((g, gi) => (
              <div key={g.title} className={gi > 0 ? 'mt-7 pt-7 border-t border-white/10' : ''}>
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <span
                    className="text-white"
                    style={{ fontSize: '17px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                  >
                    {g.title}
                  </span>
                  {g.matricula && (
                    <span
                      className="text-[#fd3eb5]"
                      style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em' }}
                    >
                      Matrícula {g.matricula}
                    </span>
                  )}
                </div>
                <div className="space-y-2.5">
                  {g.tiers.map((t) => (
                    <div key={t.name} className="flex items-baseline justify-between gap-4">
                      <span className="text-white/80" style={{ fontSize: '15px', lineHeight: 1.4 }}>{t.name}</span>
                      <span
                        className="text-white shrink-0"
                        style={{ fontSize: '15px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {t.price}
                      </span>
                    </div>
                  ))}
                </div>
                {g.schedule && g.schedule.length > 0 && (
                  <div className="mt-4 pl-4 border-l-2 border-[#009edd]/60">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={12} className="text-[#009edd]" />
                      <span
                        className="text-[#009edd]"
                        style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}
                      >
                        Horarios
                      </span>
                    </div>
                    {g.schedule.map((s) => (
                      <p key={s} className="text-white/70" style={{ fontSize: '13px', lineHeight: 1.6 }}>{s}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="px-8 lg:px-10 pb-8 pt-4 shrink-0">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="block w-full text-center py-4 rounded-full text-white transition-colors"
              style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '0.02em', background: 'linear-gradient(135deg, #009edd, #fd3eb5)' }}
            >
              Inscríbete por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ProgramsSection() {
  return (
    <section id="programs" className="bg-white py-16 lg:py-20">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <p className="text-[#009edd] mb-2" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Para toda la familia
          </p>
          <h2 className="text-[#001489]" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 700, letterSpacing: '-0.01em' }}>
            Nuestros Programas
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
          {disciplines.map((d, i) => <FlipCard key={d.title} data={d} index={i} />)}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 text-center"
        >
          <p className="text-gray-600 mb-4" style={{ fontSize: '15px' }}>
            ¿Tienes preguntas? Prueba una clase gratis con tu familia.
          </p>
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#001489] text-white px-8 py-3.5 rounded-full hover:bg-[#009edd] transition-colors"
            style={{ fontSize: '15px', fontWeight: 600 }}
          >
            Agendar Clase Gratis
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// ── Facilities ────────────────────────────────────────────────────────────────
const spaces = [
  'Pista de Patinaje',
  'Piscina Semiolímpica',
  'Gimnasio Equipado',
  'Salón de Danza',
  'Zona Infantil',
  'Cafetería Familiar',
];

function FacilitiesSection() {
  return (
    <section id="facilities" className="bg-white">
      <div className="relative w-full" style={{ height: '60vh', minHeight: '350px' }}>
        <img src={IMG.i9} alt="Instalaciones" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#001489]/60" />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2
              className="text-white"
              style={{
                fontSize: 'clamp(2rem, 6vw, 4rem)',
                fontWeight: 900,
                lineHeight: 0.95,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
              }}
            >
              Nuestras<br />Instalaciones
            </h2>
            <p className="text-white/70 mt-3" style={{ fontSize: '15px' }}>
              Espacios seguros y modernos para toda la familia.
            </p>
          </motion.div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
        <div className="border-t border-gray-200">
          {spaces.map((s, i) => (
            <motion.div
              key={s}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="flex items-center justify-between py-4 border-b border-gray-200 group"
            >
              <span className="text-[#001489] group-hover:text-[#009edd] transition-colors" style={{ fontSize: '15px', fontWeight: 500 }}>
                {s}
              </span>
              <span className="text-gray-300 group-hover:text-[#fd3eb5] transition-colors" style={{ fontSize: '20px' }}>→</span>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#001489] text-white px-8 py-3.5 rounded-full hover:bg-[#009edd] transition-colors"
            style={{ fontSize: '15px', fontWeight: 600 }}
          >
            Agenda una Visita
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
const testimonials = [
  {
    name: 'María Fernanda L.',
    role: 'Mamá — Patinaje',
    text: 'Mis hijos aman venir cada sábado. El ambiente es increíble y los profes tratan a todos como familia.',
  },
  {
    name: 'Carlos Muñoz',
    role: 'Papá — Natación',
    text: 'Empecé trayendo a mi hijo y terminé inscribiéndome yo también. Ahora nadamos juntos.',
  },
  {
    name: 'Ana Lucía V.',
    role: 'Mamá — Natación Adultos',
    text: 'Volver a nadar después de 20 años fue lo mejor que pude hacer. El equipo es muy profesional y cariñoso.',
  },
  {
    name: 'Roberto Caicedo',
    role: 'Abuelo — Patinaje',
    text: 'Traigo a mi nieto al patinaje mientras yo uso el gimnasio. Un plan perfecto para toda la familia.',
  },
];

function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);
  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const t = testimonials[current]!;
  return (
    <section id="testimonials" className="bg-[#f0f6ff] py-16 lg:py-24">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p
            className="text-[#009edd] mb-6"
            style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
          >
            Familias Felices
          </p>
          <div className="flex justify-center gap-1 mb-8">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} size={16} className="text-[#fd3eb5] fill-[#fd3eb5]" />
            ))}
          </div>
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p
              className="text-[#001489] mb-6 max-w-2xl mx-auto"
              style={{ fontSize: 'clamp(18px, 2.5vw, 24px)', lineHeight: 1.6, fontWeight: 500 }}
            >
              &ldquo;{t.text}&rdquo;
            </p>
            <p className="text-[#001489]" style={{ fontSize: '14px', fontWeight: 700 }}>{t.name}</p>
            <p className="text-gray-500" style={{ fontSize: '13px' }}>{t.role}</p>
          </motion.div>
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={prev}
              className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center hover:bg-[#001489] hover:text-white hover:border-[#001489] transition-all"
              aria-label="Anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center hover:bg-[#001489] hover:text-white hover:border-[#001489] transition-all"
              aria-label="Siguiente"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Contact (WhatsApp-only) ───────────────────────────────────────────────────
function ContactSection() {
  return (
    <section id="contact" className="bg-white">
      <div className="bg-[#001489] text-white text-center py-16 lg:py-24 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p
            className="text-[#009edd] mb-3"
            style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' }}
          >
            Inscripciones abiertas
          </p>
          <h2
            className="text-white mb-4"
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
            }}
          >
            ¡Ven con<br />tu Familia!
          </h2>
          <p className="text-gray-300 max-w-md mx-auto" style={{ fontSize: '15px', lineHeight: 1.6 }}>
            Escríbenos por WhatsApp y agenda una clase de prueba gratis.
          </p>
        </motion.div>
      </div>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white px-10 py-5 rounded-full transition-colors shadow-lg"
            style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '0.01em' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" width="22" height="22" aria-hidden="true">
              <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
            </svg>
            Escribir por WhatsApp
          </a>
          <p className="text-gray-500 mt-4" style={{ fontSize: '14px' }}>+57 315 550 2034</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-16 grid sm:grid-cols-3 gap-8 text-center"
        >
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-[#f0f6ff] rounded-full flex items-center justify-center mb-3">
              <MapPin size={20} className="text-[#009edd]" />
            </div>
            <p className="text-[#001489]" style={{ fontSize: '14px', fontWeight: 700 }}>Cl. 18 #168-5149</p>
            <p className="text-gray-500" style={{ fontSize: '13px' }}>Cali, Valle del Cauca</p>
            <p className="text-gray-400 mt-1" style={{ fontSize: '11px' }}>Antes 168-5477</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-[#f0f6ff] rounded-full flex items-center justify-center mb-3">
              <Phone size={20} className="text-[#009edd]" />
            </div>
            <p className="text-[#001489]" style={{ fontSize: '14px', fontWeight: 700 }}>+57 315 550 2034</p>
            <p className="text-gray-500" style={{ fontSize: '13px' }}>WhatsApp · Lun a Sáb</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-[#f0f6ff] rounded-full flex items-center justify-center mb-3">
              <Clock size={20} className="text-[#009edd]" />
            </div>
            <p className="text-[#001489]" style={{ fontSize: '14px', fontWeight: 700 }}>Lun – Sáb</p>
            <p className="text-gray-500" style={{ fontSize: '13px' }}>6:00 a.m. – 9:00 p.m.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function FooterSection() {
  return (
    <footer className="bg-[#001489] text-white">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          <div>
            <h4 className="text-white mb-5" style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase' }}>
              Programas
            </h4>
            <ul className="space-y-3">
              {['Natación', 'Patinaje'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-5" style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase' }}>
              Centro
            </h4>
            <ul className="space-y-3">
              {['Nuestra Historia', 'Instalaciones', 'Entrenadores', 'Horarios', 'Tarifas'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-5" style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase' }}>
              Ayuda
            </h4>
            <ul className="space-y-3">
              {['Preguntas Frecuentes', 'Política de Cancelación', 'Términos y Condiciones', 'Privacidad', 'Contáctanos'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-5" style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase' }}>
              Síguenos
            </h4>
            <ul className="space-y-3">
              {['Instagram', 'Facebook', 'YouTube'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <img src={LOGO} alt="Centro Deportivo Luz Mery Tristan" className="h-[28px] w-auto object-contain flex-shrink-0" style={{ filter: 'brightness(0) invert(1)' }} />
              <span className="text-gray-400" style={{ fontSize: '12px' }}>Cali, Colombia</span>
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              {['Guías', 'Términos de Uso', 'Política de Privacidad'].map((item) => (
                <span key={item} className="text-gray-500 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '12px' }}>{item}</span>
              ))}
              <a
                href="https://plada.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="plada-footer-mark text-gray-500 hover:text-white transition-colors"
                style={{ fontSize: '12px' }}
              >
                Built with Plada
              </a>
            </div>
            <p className="text-gray-600" style={{ fontSize: '12px' }}>
              © 2026 Centro Deportivo Luz Mery Tristán. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Ecosystem ─────────────────────────────────────────────────────────────────
const ecosystemFilters = ['Todos', 'Baile', 'Natación', 'Patinaje', 'Kids', 'Competitivo', 'Después de 5pm'];

interface EcosystemArea {
  title: string;
  subtitle: string;
  image: string;
  tags: string[];
  categories: string[];
}

const ecosystem: EcosystemArea[] = [
  {
    title: 'Kidultz Studio',
    subtitle: 'Baile, competencia y formación artística',
    image: IMG.i4,
    tags: ['Hip Hop', 'Jazz', 'Salsa', 'Ballet', 'Acrobacia'],
    categories: ['Baile', 'Kids', 'Competitivo', 'Después de 5pm'],
  },
  {
    title: 'Natación',
    subtitle: 'Bebés, niños, adultos y semilleros',
    image: IMG.i3,
    tags: ['Bebés', 'Niños', 'Adultos', 'Personalizado'],
    categories: ['Natación', 'Kids', 'Competitivo'],
  },
  {
    title: 'Patinaje',
    subtitle: 'Tapete, artístico y personalizado',
    image: IMG.i6,
    tags: ['Niños', 'Adultos', 'Artístico', 'Personalizado'],
    categories: ['Patinaje', 'Kids', 'Competitivo', 'Después de 5pm'],
  },
  {
    title: 'Wellness',
    subtitle: 'Fisioterapia, nutrición y bienestar',
    image: IMG.i10,
    tags: ['Fisioterapia', 'Nutrición', 'Psicología'],
    categories: ['Después de 5pm'],
  },
  {
    title: 'Beauty & Lifestyle',
    subtitle: 'Peluquería, ropa, drinks y más',
    image: IMG.i12,
    tags: ['Peluquería', 'Burbujitas', 'Clínica de ropa'],
    categories: ['Después de 5pm'],
  },
];

function EcosystemSection() {
  const [filter, setFilter] = useState('Todos');
  const visible = filter === 'Todos' ? ecosystem : ecosystem.filter((a) => a.categories.includes(filter));
  return (
    <section id="ecosystem" className="bg-white py-16 lg:py-20">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <p className="text-[#009edd] mb-2" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Explora el ecosistema
          </p>
          <h2 className="text-[#001489] mb-3" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 700, letterSpacing: '-0.01em' }}>
            Todo en un solo lugar
          </h2>
          <p className="text-gray-600 max-w-2xl" style={{ fontSize: '15px', lineHeight: 1.6 }}>
            Deporte, arte, wellness y lifestyle viviendo juntos. Explora actividades, horarios y servicios en segundos.
          </p>
        </motion.div>

        <p className="text-[#001489] mb-4" style={{ fontSize: '13px', fontWeight: 600 }}>
          Encuentra rápidamente lo que necesitas
        </p>

        <div className="flex flex-wrap gap-2.5 mb-8">
          {ecosystemFilters.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="px-4 py-2 rounded-full transition-all"
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: active ? '#fff' : '#001489',
                  backgroundColor: active ? '#001489' : '#f0f6ff',
                }}
              >
                {f}
              </button>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((area, i) => (
            <motion.div
              key={area.title}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-lg overflow-hidden bg-white border border-gray-100 flex flex-col"
              style={{ boxShadow: '0 1px 3px rgba(0,20,137,0.06)' }}
            >
              <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 10' }}>
                <img src={area.image} alt={area.title} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#001489]/85 to-transparent" />
                <h3 className="absolute bottom-4 left-5 text-white" style={{ fontSize: '19px', fontWeight: 700 }}>
                  {area.title}
                </h3>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <p className="text-gray-600 mb-4" style={{ fontSize: '14px', lineHeight: 1.5 }}>{area.subtitle}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {area.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full text-[#009edd] bg-[#f0f6ff]" style={{ fontSize: '11px', fontWeight: 600 }}>
                      {tag}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => document.querySelector('#programs')?.scrollIntoView({ behavior: 'smooth' })}
                  className="mt-auto text-left text-[#001489] underline underline-offset-4 decoration-[#fd3eb5] hover:text-[#009edd] transition-all"
                  style={{ fontSize: '13px', fontWeight: 600 }}
                >
                  Ver horarios y detalles →
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Today ─────────────────────────────────────────────────────────────────────
const todayActivities = [
  { title: 'Natación Niños', schedule: 'Lun a Vie 3:00 – 5:00 p.m.', price: 'Desde $272.600' },
  { title: 'Hip Hop Intermedio', schedule: 'Martes y jueves 5:00 p.m.', price: '$270.000' },
  { title: 'Patinaje Adultos', schedule: 'Lun a Vie 6:30 p.m.', price: 'Desde $144.800' },
];

function TodaySection() {
  return (
    <section id="today" className="bg-[#f0f6ff] py-16 lg:py-20">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <p className="text-[#009edd] mb-2" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Hoy
          </p>
          <h2 className="text-[#001489] mb-2" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 700, letterSpacing: '-0.01em' }}>
            ¿Qué está pasando hoy?
          </h2>
          <p className="text-gray-600" style={{ fontSize: '15px' }}>
            Actividades y experiencias activas en el centro.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {todayActivities.map((act, i) => (
            <motion.div
              key={act.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-lg p-6 flex flex-col"
              style={{ boxShadow: '0 1px 3px rgba(0,20,137,0.06)' }}
            >
              <h3 className="text-[#001489] mb-4" style={{ fontSize: '18px', fontWeight: 700 }}>{act.title}</h3>
              <div className="flex items-center gap-2 mb-2">
                <Clock size={15} className="text-[#009edd]" />
                <span className="text-gray-600" style={{ fontSize: '13px' }}>{act.schedule}</span>
              </div>
              <p className="text-[#001489] mb-6" style={{ fontSize: '14px', fontWeight: 700 }}>{act.price}</p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto block w-full text-center py-2.5 rounded-full text-white transition-colors"
                style={{ fontSize: '14px', fontWeight: 600, background: 'linear-gradient(135deg, #009edd, #fd3eb5)' }}
              >
                Reservar
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Closing CTA ───────────────────────────────────────────────────────────────
function ClosingCTA() {
  return (
    <section className="bg-[#001489] text-white text-center py-16 lg:py-24 px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl mx-auto"
      >
        <h2
          className="text-white mb-4"
          style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.02em', textTransform: 'uppercase' }}
        >
          Mucho más que un<br />centro deportivo
        </h2>
        <p className="text-gray-300 mb-8" style={{ fontSize: '16px', lineHeight: 1.6 }}>
          Un espacio donde familias, atletas, artistas y comunidad se encuentran.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <button
            onClick={() => document.querySelector('#ecosystem')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-[#001489] px-8 py-3.5 rounded-full hover:bg-[#f0f6ff] transition-colors"
            style={{ fontSize: '15px', fontWeight: 600 }}
          >
            Encontrar actividad
          </button>
          <button
            onClick={() => document.querySelector('#programs')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-white border-2 border-[#009edd] px-8 py-3.5 rounded-full hover:bg-[#009edd] transition-colors"
            style={{ fontSize: '15px', fontWeight: 600 }}
          >
            Explorar horarios
          </button>
        </div>
      </motion.div>
    </section>
  );
}

// ── Help Button (floating) ─────────────────────────────────────────────────────
function HelpButton() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full text-white shadow-2xl hover:scale-105 transition-transform"
      style={{ background: 'linear-gradient(135deg, #009edd, #fd3eb5)', fontSize: '14px', fontWeight: 600 }}
      aria-label="Necesitas ayuda"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" width="18" height="18" aria-hidden="true">
        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
      </svg>
      <span className="hidden sm:inline">Necesitas ayuda?</span>
    </a>
  );
}

// ── Root component ────────────────────────────────────────────────────────────
const SCROLL_CSS = `
.centro-scroll {
  overflow-y: scroll !important;
  scrollbar-width: thin;
  scrollbar-color: #fd3eb5 rgba(255,255,255,0.12);
  scrollbar-gutter: stable;
}
.centro-scroll::-webkit-scrollbar {
  width: 10px;
  -webkit-appearance: none;
}
.centro-scroll::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.08);
  border-radius: 8px;
}
.centro-scroll::-webkit-scrollbar-thumb {
  background: #fd3eb5;
  border-radius: 8px;
  border: 2px solid rgba(0,20,137,1);
  min-height: 40px;
}
.centro-scroll::-webkit-scrollbar-thumb:hover {
  background: #e82fa0;
}
`;

export default function CentroDeportivoLmt() {
  useEffect(() => {
    document.title = 'Centro Deportivo Luz Mery Tristán';
    const existing = document.head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');
    existing.forEach((el) => el.parentNode?.removeChild(el));

    // Light-mode favicon (navy logo on white bg)
    const lightIcon = document.createElement('link');
    lightIcon.rel = 'icon';
    lightIcon.type = 'image/png';
    lightIcon.media = '(prefers-color-scheme: light)';
    lightIcon.href = FAVICON_LIGHT;
    document.head.appendChild(lightIcon);

    // Dark-mode favicon (white logo on transparent bg)
    const darkIcon = document.createElement('link');
    darkIcon.rel = 'icon';
    darkIcon.type = 'image/png';
    darkIcon.media = '(prefers-color-scheme: dark)';
    darkIcon.href = FAVICON_DARK;
    document.head.appendChild(darkIcon);

    // Apple touch icon (used by iOS home screen — always the light/colored version)
    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = FAVICON_LIGHT;
    document.head.appendChild(apple);
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <style>{SCROLL_CSS}</style>
      <Navbar />
      <main>
        <HeroSection />
        <AboutSection />
        <EcosystemSection />
        <ProgramsSection />
        <TodaySection />
        <FacilitiesSection />
        <TestimonialsSection />
        <ClosingCTA />
        <ContactSection />
      </main>
      <FooterSection />
      <HelpButton />
    </div>
  );
}
